import React, { useState, useEffect } from 'react';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { LessonCard, LessonDialog } from '@/components/ai-devquest';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
 
import { AlertCircle, BookOpen, Clock, ChevronDown, ChevronRight, Calendar } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Lesson, Category, LessonFormat } from '@/types/ai-devquest';
import { aiDevQuestService } from '@/services/aiDevQuestService';
import { employeeNamesService } from '@/services/employeeNames';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

/**
 * AI DevQuest Dashboard Page
 */
const AIDevQuestDashboard: React.FC = () => {
  usePageTitle('AI DevQuest');
  
  const { isAuthenticated, isLoading: authLoading, userEmail } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string>('');
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [userProgress, setUserProgress] = useState<Record<string, boolean>>({});
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  
  // Format name from email
  const formatName = (email: string): string => {
    return email
      .split('@')[0]
      .split('.')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };
  
  // Handle marking a lesson as complete
  const handleMarkLessonComplete = async (lessonId: string) => {
    if (!userEmail) return;
    
    try {
      // Update local state immediately for better UX
      setUserProgress(prev => ({
        ...prev,
        [lessonId]: true
      }));
      
      // No need to call the service here as it's already called in the LessonDialog component
    } catch (err) {
      console.error('Error marking lesson as complete:', err);
      // Revert the optimistic update if there's an error
      setUserProgress(prev => ({
        ...prev,
        [lessonId]: false
      }));
    }
  };
  
  // Toggle category expansion
  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };
  
  // Load data
  useEffect(() => {
    const loadData = async () => {
      if (!isAuthenticated || !userEmail) return;
      
      try {
        setIsLoading(true);
        setError(null);
        
        // Get user display name
        const userName = await employeeNamesService.getNameFromEmail(userEmail);
        setDisplayName(userName || formatName(userEmail));
        
        // Initialize default categories if needed
        await aiDevQuestService.initializeDefaultCategories();
        
        // Load categories, lessons, and user progress
        const [categoriesData, lessonsData, userProgressData] = await Promise.all([
          aiDevQuestService.getCategories(),
          aiDevQuestService.getLessons(),
          aiDevQuestService.getUserProgress(userEmail)
        ]);
        
        setCategories(categoriesData);
        setLessons(lessonsData);
        
        // Convert user progress to a map for easier lookup
        const progressMap: Record<string, boolean> = {};
        userProgressData.forEach(progress => {
          progressMap[progress.lessonId] = progress.completed;
        });
        setUserProgress(progressMap);
      } catch (err) {
        console.error('Error loading AI DevQuest data:', err);
        setError('Failed to load data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, [isAuthenticated, userEmail]);
  
  // Group lessons by category
  const lessonsByCategory = lessons.reduce<Record<string, Lesson[]>>((acc, lesson) => {
    if (!acc[lesson.category]) {
      acc[lesson.category] = [];
    }
    acc[lesson.category].push(lesson);
    return acc;
  }, {});
  
  // Calculate total estimated time for each category
  const getCategoryTotalTime = (categoryId: string): number => {
    const categoryLessons = lessonsByCategory[categoryId] || [];
    return categoryLessons.reduce((total, lesson) => total + lesson.estimatedTime, 0);
  };
  
  // Format time (minutes to hours and minutes)
  const formatTime = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (remainingMinutes === 0) {
      return `${hours} hr`;
    }
    
    return `${hours} hr ${remainingMinutes} min`;
  };
  
  // Calculate progress for a category
  const getCategoryProgress = (categoryId: string): { completed: number, total: number } => {
    const categoryLessons = lessonsByCategory[categoryId] || [];
    const completedLessons = categoryLessons.filter(lesson => userProgress[lesson.id]).length;
    
    return {
      completed: completedLessons,
      total: categoryLessons.length
    };
  };
  
  // Get the most recent lesson for a category
  const getLastAddedLesson = (categoryId: string): Lesson | null => {
    const categoryLessons = lessonsByCategory[categoryId] || [];
    if (categoryLessons.length === 0) return null;
    
    // Sort lessons by createdAt or updatedAt date (newest first)
    return [...categoryLessons].sort((a, b) => {
      const dateA = a.updatedAt || a.createdAt;
      const dateB = b.updatedAt || b.createdAt;
      
      if (!dateA) return 1;
      if (!dateB) return -1;
      
      // Compare timestamps (newest first)
      const timeA = dateA.toDate ? dateA.toDate().getTime() : 0;
      const timeB = dateB.toDate ? dateB.toDate().getTime() : 0;
      return timeB - timeA;
    })[0];
  };
  
  // Get greeting based on time of day
  const getGreeting = (): string => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };
  
  // Format date from Firebase Timestamp
  const formatDate = (timestamp?: any): string => {
    if (!timestamp) return 'No date';
    
    let date;
    if (timestamp.toDate) {
      // Firebase Timestamp
      date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else {
      return 'No date';
    }
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  
  if (error) {
    return (
      <div className="p-4 md:p-8 max-w-6xl mx-auto">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <div className="text-center mt-4">
          <Button onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto">
      <div className="mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">AI DevQuest</h1>
        <p className="text-sm md:text-base text-gray-500 dark:text-gray-400">Coding with AI Ecourse</p>
      </div>
      
      {/* User Greeting */}
      <div className="mb-6 md:mb-8">
        <h2 className="text-xl md:text-2xl font-semibold text-gray-800 dark:text-gray-200">
          {getGreeting()}, {displayName}!
        </h2>
      </div>
      
      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lessons Column */}
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 flex items-center">
              <BookOpen className="h-5 w-5 mr-2 text-gray-600 dark:text-gray-400" />
              Lessons
            </h3>
          </div>
          
          {/* Category Cards */}
          {isLoading ? (
            // Loading Skeletons
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Card key={i}>
                  <CardHeader className="p-4">
                    <Skeleton className="h-6 w-1/3 mb-2" />
                    <Skeleton className="h-4 w-1/4" />
                  </CardHeader>
                </Card>
              ))}
            </div>
          ) : categories.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No categories found. Please add categories in the admin panel.
            </div>
          ) : (
            <div className="space-y-4">
              {categories.map(category => {
                const { completed, total } = getCategoryProgress(category.id);
                const totalTime = getCategoryTotalTime(category.id);
                const isExpanded = expandedCategories[category.id] || false;
                const lastLesson = getLastAddedLesson(category.id);
                const categoryLessons = lessonsByCategory[category.id] || [];
                
                return (
                  <Collapsible
                    key={category.id}
                    open={isExpanded}
                    onOpenChange={() => toggleCategory(category.id)}
                    className="border dark:border-0 rounded-lg overflow-hidden"
                  >
                    <CollapsibleTrigger asChild>
                      <Card className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 border-0">
                        <CardHeader className="p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <CardTitle className="text-lg flex items-center">
                                {category.name}
                                {isExpanded ? (
                                  <ChevronDown className="h-5 w-5 ml-2" />
                                ) : (
                                  <ChevronRight className="h-5 w-5 ml-2" />
                                )}
                              </CardTitle>
                              <CardDescription className="flex items-center mt-1 space-x-4">
                                <span>{completed}/{total} lessons</span>
                                <span className="flex items-center">
                                  <Clock className="h-3 w-3 mr-1" />
                                  {formatTime(totalTime)}
                                </span>
                              </CardDescription>
                            </div>
                            <div className="text-right">
                              {lastLesson ? (
  
                              <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                                 <Calendar className="h-3 w-3 mr-1" />
                                  <span>Last updated: {formatDate(lastLesson.updatedAt || lastLesson.createdAt)}</span>
                                </div>
                              ) : (
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                   No lessons yet
                                </div>
                              )}
                            </div>
                          </div>
                        </CardHeader>
                      </Card>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="mt-4 mb-2 py-6 px-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        {categoryLessons.length === 0 ? (
                          <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                            No lessons found for this category.
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 gap-6 max-w-3xl mx-auto">
                            {categoryLessons.map((lesson) => (
                              <LessonCard
                                key={lesson.id}
                                lesson={lesson}
                                isCompleted={!!userProgress[lesson.id]}
                                onClick={() => setSelectedLesson(lesson)}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Progress Column */}
        <div>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center">
            <Clock className="h-5 w-5 mr-2 text-gray-600 dark:text-gray-400" />
            Progress
          </h3>
          
          {isLoading ? (
            // Loading Skeletons for Progress Cards
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-4">
                    <Skeleton className="h-6 w-3/4 mb-2" />
                    <Skeleton className="h-4 w-1/2 mb-2" />
                    <Skeleton className="h-2 w-full rounded-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {categories.map(category => {
                const { completed, total } = getCategoryProgress(category.id);
                const progressPercentage = total > 0 ? Math.round((completed / total) * 100) : 0;
                
                return (
                  <Card key={category.id}>
                    <CardContent className="p-4">
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                        {category.name}
                      </h4>
                      <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        {completed}/{total} lessons
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div 
                          className="bg-blue-600 dark:bg-blue-500 h-2 rounded-full" 
                          style={{ width: `${progressPercentage}%` }}
                        />
                      </div>
                      
                      {/* Progress Percentage */}
                      <div className="text-xs text-right mt-1 text-gray-500 dark:text-gray-400">
                        {progressPercentage}%
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              
              {categories.length === 0 && (
                <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                  No progress data available.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Lesson Dialog */}
      {selectedLesson && (
        <LessonDialog
          lesson={selectedLesson}
          isOpen={!!selectedLesson}
          onClose={() => setSelectedLesson(null)}
          onComplete={handleMarkLessonComplete}
          isCompleted={!!userProgress[selectedLesson.id]}
        />
      )}
    </div>
  );
};

export default AIDevQuestDashboard;